import React from "react";
import { AlertTriangle, CheckCircle2, Info, Lightbulb, OctagonAlert } from "lucide-react";
import { useTranslation, useFormat } from "../../i18n";

const SEVERITY = {
  critical: { icon: OctagonAlert, cls: "bg-rose-50 border-rose-200 text-rose-800", iconCls: "text-rose-600" },
  warning: { icon: AlertTriangle, cls: "bg-amber-50 border-amber-200 text-amber-900", iconCls: "text-amber-600" },
  good: { icon: CheckCircle2, cls: "bg-emerald-50 border-emerald-200 text-emerald-900", iconCls: "text-emerald-600" },
  info: { icon: Info, cls: "bg-sky-50 border-sky-200 text-sky-900", iconCls: "text-sky-600" },
};

/**
 * Renders backend insight keys as sentences. Params are pre-formatted here so
 * the translation strings only ever see display-ready values.
 */
const InsightsPanel = ({ insights, onNavigate, compact = false }) => {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  if (!insights || insights.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 bg-white rounded-2xl border border-slate-200 p-4">
        <Lightbulb className="w-4 h-4 text-purple-500" aria-hidden="true" />
        {t("insights.none")}
      </div>
    );
  }
  const format = (params = {}) => {
    const out = {};
    Object.entries(params).forEach(([k, v]) => {
      if (typeof v === "number") {
        if (/share|rate|pct|change/.test(k)) out[k] = fmt.number(v / 100, { style: "percent", maximumFractionDigits: 0 });
        else if (/value|available|liability/.test(k)) out[k] = fmt.number(v, { maximumFractionDigits: 0 });
        else out[k] = fmt.number(v, { maximumFractionDigits: 1 });
      } else out[k] = v;
    });
    return out;
  };
  return (
    <ul className={`grid gap-2 ${compact ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"}`}>
      {insights.map((i, idx) => {
        const s = SEVERITY[i.severity] || SEVERITY.info;
        const Icon = s.icon;
        return (
          <li key={`${i.key}-${idx}`}>
            <button
              type="button"
              onClick={onNavigate ? () => onNavigate(i.section) : undefined}
              className={`w-full text-left flex items-start gap-2.5 rounded-xl border px-3 py-2.5 text-sm ${s.cls} ${onNavigate ? "hover:shadow-sm" : "cursor-default"}`}
            >
              <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${s.iconCls}`} aria-hidden="true" />
              <span>
                <span className="sr-only">{t(`insights.severity.${i.severity}`)}: </span>
                {t(`insights.${i.key}`, format(i.params))}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

export default InsightsPanel;
